import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import jobData from "../jobPosts.json";

const SectionList = ({ title, items }) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Row className="mt-4">
      <Col>
        <section className="border rounded-3 bg-white p-4 shadow-sm">
          <h3 className="h4 mb-3">{title}</h3>
          <ul className="mb-0 ps-3">
            {items.map((item, index) => (
              <li className="mb-2 ps-1" key={`${title}-${index}`}>
                {item}
              </li>
            ))}
          </ul>
        </section>
      </Col>
    </Row>
  );
};

const JobPost = ({ jobId }) => {
  const job = jobData.find((role) => role.id === jobId);
  const commonAbout = [
    "We are a fast-growing startup building innovative digital and mobile-first products. Our mission is to create seamless, user-centric experiences that solve real-world problems. We value curiosity, ownership, and a bias for action."
  ];

  if (!job) {
    return (
      <Container className="job-post">
        <Row>
          <Col>
            <p className="text-center">The requested role could not be found.</p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="job-post py-4 py-md-5" id={`job-${job.id}`}>
      <Row className="justify-content-center">
        <Col>
          <header
            className="job-hero rounded-4 px-4 py-5 text-center text-white shadow-sm"
            style={{
              background:
                "linear-gradient(135deg, rgb(250, 125, 41), rgb(220, 86, 32))",
            }}
          >
            <p className="job-hero-eyebrow mb-2 text-uppercase fw-semibold small">Join Our Team</p>
            <h1 className="job-hero-title display-5 fw-bold mb-4">{job.title}</h1>
            <div className="job-hero-badges d-flex flex-wrap justify-content-center gap-2">
              <span className="badge rounded-pill job-hero-badge px-3 py-2">
                {job.type}
              </span>
              <span className="badge rounded-pill job-hero-badge px-3 py-2">
                {job.location}
              </span>
              {job.duration ? (
                <span className="badge rounded-pill job-hero-badge px-3 py-2">
                  Duration: {job.duration}
                </span>
              ) : null}
            </div>
          </header>
        </Col>
      </Row>
      
      <Row className="mt-3">
        <Col>
          <section className="border rounded-3 bg-white p-4 shadow-sm">
            <h2 className="h4 mb-3">About Us</h2>
            {commonAbout.map((paragraph, index) => (
              <p className="mb-0 job-copy" key={`about-common-${index}`}>
                {paragraph}
              </p>
            ))}
          </section>
        </Col>
      </Row>

      {job.overview ? (
        <Row className="mt-4">
          <Col>
            <section className="border rounded-3 bg-white p-4 shadow-sm">
              <h2 className="h4 mb-3">Position Overview</h2>
              <p className="mb-0 job-copy">{job.overview}</p>
            </section>
          </Col>
        </Row>
      ) : null}
      {job.stipend ? (
        <Row className="mt-4">
          <Col>
            <section className="border rounded-3 bg-white p-4 shadow-sm">
              <h2 className="h4 mb-3">Stipend</h2>
              <p className="mb-0 job-copy" style={{ whiteSpace: "pre-line" }}>
                {job.stipend}
              </p>
            </section>
          </Col>
        </Row>
      ) : null}
      
      <SectionList title="Key Responsibilities:" items={job.responsibilities} />
      <SectionList title="Eligibility:" items={job.eligibility} />
      <SectionList title="Requirements:" items={job.requiredSkills} />
      <SectionList title="Preferred Skills:" items={job.preferredSkills} />
      <SectionList title="Benefits:" items={job.benefits} />

      {job.applyEmail ? (
        <Row className="my-4">
          <Col>
            <section
              className="rounded-3 p-4 shadow-sm"
              style={{ backgroundColor: "rgba(250, 125, 41, 0.08)" }}
            >
              <h2 className="h3 mb-3">How to Apply</h2>
              <p className="job-copy mb-4">
                Interested candidates are invited to submit their resume, a
                brief cover letter, and any relevant portfolio or project
                links.
              </p>

              <div className="d-flex flex-column gap-3">
                <a
                  href={`mailto:${job.applyEmail}`}
                  className="d-flex align-items-center gap-3 rounded-3 border bg-white p-3 text-decoration-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    className="bi bi-envelope-fill text-danger flex-shrink-0"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414zM0 4.697v7.104l5.803-3.558zM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144L9.239 8.83 8 9.586zm3.436-.586L16 11.801V4.697z" />
                  </svg>
                  <span>
                    <small className="d-block job-label">Email your application</small>
                    <strong className="job-value">{job.applyEmail}</strong>
                  </span>
                </a>

                {job.applyWhatsApp ? (
                  <a
                    href={`https://wa.me/${job.applyWhatsApp.replace(/[^\d]/g, "")}`}
                    className="d-flex align-items-center gap-3 rounded-3 border bg-white p-3 text-decoration-none"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      className="bi bi-whatsapp text-success flex-shrink-0"
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                    >
                      <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93a7.9 7.9 0 0 0-2.327-5.607m-5.607 12.2a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.25a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.591-6.592 6.591m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.066-.315-.099-.445.099-.133.197-.514.646-.627.775-.116.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.984-.59-.525-.986-1.175-1.103-1.372-.116-.198-.013-.306.087-.404.089-.088.197-.232.296-.347.1-.116.133-.198.198-.33.066-.133.033-.248-.017-.347-.05-.099-.445-1.075-.61-1.47-.16-.389-.323-.335-.445-.34-.116-.006-.248-.006-.38-.006a.73.73 0 0 0-.529.248c-.182.198-.691.677-.691 1.654s.708 1.916.81 2.049c.098.132 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.337-.943.164-.462.164-.86.116-.943-.05-.082-.182-.132-.38-.23" />
                    </svg>
                    <span>
                      <small className="d-block job-label">Message us on WhatsApp</small>
                      <strong className="job-value">{job.applyWhatsApp}</strong>
                    </span>
                  </a>
                ) : null}
              </div>
            </section>
          </Col>
        </Row>
      ) : null}
    </Container>
  );
};

export default JobPost;
